import { ColumnsBlock, EmailBlock, EmailLayout, newBlockId } from "./emailBuilder.model";

// Paths: [i] - a root block; [i, col, j] - block j of column col of the columns block i.
// Containers: [] - the root; [i, col] - a column. Columns can't be nested.
export type BlockPath = number[];

export const cloneLayout = (layout: EmailLayout): EmailLayout => JSON.parse(JSON.stringify(layout));

export const samePath = (a: BlockPath | null, b: BlockPath | null) =>
  !!a && !!b && a.length === b.length && a.every((value, i) => value === b[i]);

export const containerOf = (path: BlockPath): BlockPath =>
  path.length === 1 ? [] : path.slice(0, 2);

export const pathIn = (container: BlockPath, index: number): BlockPath => [...container, index];

export const getContainer = (layout: EmailLayout, container: BlockPath): EmailBlock[] => {
  if (container.length === 0) return layout.blocks;
  const parent = layout.blocks[container[0]] as ColumnsBlock;
  return parent.columns[container[1]].blocks;
};

export const getBlock = (layout: EmailLayout, path: BlockPath | null): EmailBlock | null => {
  if (!path || path.length === 0) return null;
  return getContainer(layout, containerOf(path))[path[path.length - 1]] ?? null;
};

// The helpers below change the layout they get - call them on a clone

export const removeBlock = (layout: EmailLayout, path: BlockPath): BlockPath | null => {
  const container = containerOf(path);
  const blocks = getContainer(layout, container);
  const index = path[path.length - 1];
  blocks.splice(index, 1);
  const next = Math.min(index, blocks.length - 1);
  if (next >= 0) return pathIn(container, next);
  return container.length ? [container[0]] : null;
};

export const moveBlock = (layout: EmailLayout, path: BlockPath, direction: -1 | 1): BlockPath => {
  const container = containerOf(path);
  const blocks = getContainer(layout, container);
  const index = path[path.length - 1];
  const target = index + direction;
  if (target < 0 || target >= blocks.length) return path;
  const [block] = blocks.splice(index, 1);
  blocks.splice(target, 0, block);
  return pathIn(container, target);
};

const regenerateIds = (block: EmailBlock) => {
  block.id = newBlockId();
  if (block.type === "columns")
    block.columns.forEach((column) => column.blocks.forEach(regenerateIds));
};

export const duplicateBlock = (layout: EmailLayout, path: BlockPath): BlockPath => {
  const container = containerOf(path);
  const blocks = getContainer(layout, container);
  const index = path[path.length - 1];
  const copy: EmailBlock = JSON.parse(JSON.stringify(blocks[index]));
  regenerateIds(copy);
  blocks.splice(index + 1, 0, copy);
  return pathIn(container, index + 1);
};

// Puts a new block or moves an existing one (from) to position index of a container; returns its new path
export const insertBlock = (
  layout: EmailLayout,
  container: BlockPath,
  index: number,
  block: EmailBlock | null,
  from: BlockPath | null
): BlockPath | null => {
  let toInsert = block;
  let targetIndex = index;
  if (from) {
    const fromContainer = containerOf(from);
    const fromIndex = from[from.length - 1];
    if (samePath(fromContainer, container) && (fromIndex === index || fromIndex + 1 === index)) {
      return from;
    }
    toInsert = getContainer(layout, fromContainer).splice(fromIndex, 1)[0];
    if (samePath(fromContainer, container) && fromIndex < index) targetIndex -= 1;
    // A move out of an earlier root block shifts the root index of a column container
    if (from.length === 1 && container.length === 2 && from[0] < container[0]) {
      container = [container[0] - 1, container[1]];
    }
  }
  if (!toInsert) return null;
  const blocks = getContainer(layout, container);
  const safeIndex = Math.max(0, Math.min(targetIndex, blocks.length));
  blocks.splice(safeIndex, 0, toInsert);
  return pathIn(container, safeIndex);
};
